import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { showModal } from 'modal.react.js';
import { mergeDeepRight } from 'ramda';
import { useForm, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import { ISelectOption } from 'components/shared/BaseFormComponents/Select/types';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { ETripEvent } from 'detailsv2/structure';
import { itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { infoLoadTypes } from '../../../../../../../constants';
import { getOptionsLoader } from '../../../../functions';
import { useTripEventDefaultValues } from '../../../shared';
import { useInfoWizardContext } from '../../context';
import { infoValidationSchema } from '../../validation';
import * as types from './types';

export function useInfoForm() {
  const { tripEvent, data, updateData, nextStep, onSubmit } = useInfoWizardContext();
  const organizedBy = useSelector(itineraryScheduleTypeSelector);

  const defaultValues = mergeDeepRight(tripEvent, data.default);
  const methods = useForm<types.FieldValues>({
    defaultValues: {
      ...defaultValues,
      attachedPage: defaultValues?.attachedPages?.[0],
    },
    resolver: yupResolver(infoValidationSchema(organizedBy)),
    mode: 'onTouched',
  });

  const loadOptions = getOptionsLoader(infoLoadTypes, createOptionFromPageV2);

  const { watch, clearErrors, setValue } = methods;
  const attachedPage = watch('attachedPage');
  const isVisibleFormBody = !!attachedPage;

  const isNeedToShowTooltip =
    tripEvent.id &&
    tripEvent.showChangedRoomOrPageTooltip &&
    tripEvent?.attachedPages?.[0]?.id !== watch('attachedPages')?.[0]?.id;

  const tripEventDeafultValuesController = useTripEventDefaultValues<types.FieldValues>({
    eventType: ETripEvent.Information,
    methods,
    handleSubmit: onSubmit,
    isTripEventCreated: !!tripEvent.id,
  });

  React.useEffect(() => {
    if (tripEventDeafultValuesController?.savedDefaultValues?.data) return;

    const defaultTitle = defaultValues?.data?.title;
    const name = attachedPage?.name || null;

    if (name && !defaultTitle) {
      clearErrors();
      setValue('data.title', name);
    }
  }, [attachedPage]);

  const onCreateOption = async (inputValue: ISelectOption) => {
    const attachedPage = {
      id: inputValue.id?.toString() || undefined,
      name: inputValue.name,
    };
    updateData('default', { attachedPages: [attachedPage] });
    nextStep();
  };

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  return {
    loadOptions,
    methods,
    openPagePreview,
    onCreateOption,
    isVisibleFormBody,
    isNeedToShowTooltip,
    tripEventDeafultValuesController,
  };
}

export const useInfoFormContext = () => {
  const methods = useFormContext<types.FieldValues>();
  return methods;
};
